<template>
  <div class="content-block dx-card">
    <h2 class="content-block">Route Details</h2>
  </div>
  <div v-if="loading" class="loading-overlay">
    LOADING...
    <span class="loader"></span>
  </div>
  <div v-else>
  <div class="content-block dx-card">
    <DxToolbar class="menubar">
      <DxItem :options="cancelButtonOptions" location="before" widget="dxButton" />
      <DxItem :options="saveButtonOptions" location="before" widget="dxButton" />
      <DxItem :options="deleteButtonOptions" location="before" widget="dxButton" />
      <DxItem :options="editButtonOptions" location="before" widget="dxButton" />
    </DxToolbar>
  </div>

  <div class="content-block dx-card responsive-paddings">
    <DxForm id="form" :form-data="routes" :label-mode="outside" :read-only="false" :show-colon-after-label="true"
      :label-location="top" :col-count="1" :min-col-width="300" :width="null" :key="routes.routeId"
      :items="['routeId', 'routeName']">
      <DxSimpleItem data-field="routeId" :editor-options="{ readOnly: true }" />
      <DxSimpleItem data-field="routeName" :editor-options="{ readOnly: !isEditable }" />
    </DxForm>

    <RouteFilterPanel :routes="routes" :is-editable="isEditable" :all-operators="operatorList" :all-areas="areaList"
      :all-products="productList" :containers="containers" @update:routes="onRoutesUpdate" />
  </div>
  <div class="content-block dx-card responsive-paddings">
    <DxDataGrid class="dx-card wide-card" :data-source="containers" :key-expr="'containerId'"
      :key="`grid-${isEditable}-${isDeletingAllowed}`" :filter-value="routeFilters"
      @update:routeFilters="routeFilters = $event" :filter-sync-enabled="false" :hover-state-enabled="true"
      :show-borders="true" :columns-auto-width="true" :column-min-width="50" :allow-column-resizing="true"
      :column-resizing-mode="nextColumn" :export="{ enabled: true }" style="max-height: 80vh"
      @initialized="saveGridInstance" @exporting="onExporting" @row-prepared="onRowPrepared">
      <DxRowDragging :allow-reordering="isEditable" :on-reorder="onReorder"
        :show-drag-icons="showDragIcons && isEditable" />
      <DxSorting mode="none" />
      <DxPager :show-info="true" :show-page-size-selector="true" :allowed-page-sizes="[20, 50, 100]"
        :show-navigation-buttons="true" />
      <DxPaging :enabled="true" :page-size="20" :page-index="0" />
      <DxLoadPanel :enabled="true" />
      <DxScrolling mode="standard" />
      <DxColumnChooser :enabled="true" mode="select" />
      <DxHeaderFilter :visible="true" />
      <!-- <DxColumnFixing :enabled="true" /> -->
      <DxEditing :use-icons="true" mode="row" :confirm-delete="false" />

      <DxDataGridToolbar>
        <DxDataGridItem location="after" name="exportButton" />
        <DxDataGridItem location="after" name="columnChooserButton" />
        <DxDataGridItem location="after" template="removeFilterRecordTemplate" />
      </DxDataGridToolbar>
      <template #removeFilterRecordTemplate>
        <DxButton icon="filter" @click="removeFilter" />
      </template>

      <DxColumn data-field="containerName" caption="Surface" />
      <DxColumn data-field="operator" caption="Operator" />
      <DxColumn data-field="area" caption="Area" />
      <DxColumn data-field="containerType" caption="Type" />
      <DxColumn data-field="product" caption="Product" alignment="left" :visible="true" />
      <DxColumn data-field="dte" caption="DTE" alignment="left" :visible="true" />
      <DxColumn caption="Ullage" dataType="number" alignment="left"
        :calculate-cell-value="(row) => { const capacity = Number(row['capacity']); const volume = Number(row['volume (l)']); if (isNaN(capacity) || isNaN(volume)) return null; return capacity - volume; }" />
      <DxColumn v-for="prop in properties" :key="prop" :data-field="prop" :caption="prop" alignment='left'
        min-width="100" :visible="false" />
      <DxColumn caption="" :width="80" alignment="center" :cell-template="'checkboxTemplate'" />

      <template #checkboxTemplate="{ data }">
        <DxCheckBox v-if="data && data.row && data.row.data && data.row.data.containerId"
          :value="!isDeletedContainer(data.row.data.containerId) && !data.row.data.isAdditional"
          @value-changed="onCheckboxChange($event, data)" :disabled="!isEditable" :visible="isDeletingAllowed" />
      </template>
    </DxDataGrid>
  </div>
  <DxLoadPanel v-model:visible="loadingVisible" :message="Loading" :show-indicator="true" :show-pane="true"
    :shading="true" :hide-on-outside-click="false" :on-shown="onLoadPanelShown" :on-hidden="onLoadPanelHidden"
    shading-color="rgba(0,0,0,0.4)" />
  <DxToast v-model:visible="toastVisible" v-model:message="toastMessage" v-model:type="toastType"
    position="bottom center" />
  </div>
</template>

<script>
import { ref } from "vue";
import DxForm, { DxSimpleItem } from "devextreme-vue/form";
import DxToolbar, { DxItem } from "devextreme-vue/toolbar";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import { DxToast } from "devextreme-vue/toast";
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
  DxPaging,
  DxPager,
  DxRowDragging,
  DxSorting,
  DxEditing,
  DxColumnChooser,
  DxHeaderFilter,
  DxToolbar as DxDataGridToolbar,
  DxItem as DxDataGridItem,
} from "devextreme-vue/data-grid";
import { DxButton } from "devextreme-vue/button";
import { DxCheckBox } from "devextreme-vue/check-box";
// DxTagBox is imported for its side effect: loading it registers the dxTagBox
// widget so the grid's tagBox editor renders. The binding is meant to be unused.
// eslint-disable-next-line no-unused-vars
import { DxTagBox } from "devextreme-vue";
import RouteFilterPanel from "./route-filter-panel.vue";

export default {
  components: {
    DxForm,
    DxSimpleItem,
    DxToolbar,
    DxItem,
    DxLoadPanel,
    DxToast,
    DxDataGrid,
    DxColumn,
    DxScrolling,
    DxPaging,
    DxPager,
    DxRowDragging,
    DxSorting,
    DxButton,
    DxCheckBox,
    DxEditing,
    DxColumnChooser,
    DxHeaderFilter,
    DxDataGridToolbar,
    DxDataGridItem,
    RouteFilterPanel,
  },
  created() {
    this.loadPageData();
  },
  data() {
    return {
      loading: true,
      showDragIcons: ref(true),
      dataGridInstance: null,
      containers: [],
      routes: {
        routeId: 0,
        routeName: "",
        operators: [],
        areas: [],
        products: [],
        sortedOrder: [],
        deletedContainers: [],
      },
      routeFilters: [],
      routeConfig: {
        isDeleteContainersEnabled: false,
      },
      isDeleteIconVisible: false,
      loadingVisible: false,
      toastVisible: false,
      toastMessage: "",
      toastType: "info",
      toastPosition: {
        at: "bottom",
        my: "top",
        of: "#pwreset",
      },
      cancelButtonOptions: {
        icon: "revert",
        onClick: () => {
          this.$router.go(-1);
        },
      },
      deleteButtonOptions: {
        icon: "close",
        onClick: () => {
          let result = confirm(
            `<div style="text-align: center;">
              <b>Are you sure you want to delete this route?</b>
              <p><strong>Name:</strong> ${this.routes.routeName}</p>
              <p><strong>ID:</strong> ${this.routes.routeId}</p>
              <p><strong>Warning:</strong> This action cannot be undone!</p>
            </div>`,
            "Delete Route?"
          );
          result.then((dialogResult) => {
            this.delete(dialogResult);
          });
        },
      },
      saveButtonOptions: {
        icon: "save",
        onClick: () => {
          this.loadingVisible = true;
          if (this.routes.routeId == 0) {
            this.$store.dispatch("routes/addRoute", this.routes);
            this.$emit("recordUpdated");
          } else {
            this.$store.dispatch("routes/updateRoute", this.routes);
            this.$emit("recordUpdated");
          }
          setTimeout(() => {
            const rc = this.$store.getters["routes/getResponseCode"];

            if (rc >= 200 && rc <= 299) {
              this.toastType = "success";
              this.toastMessage = "Record saved (" + rc + ").";
              this.toastVisible = true;
              this.routes = this.$store.getters["routes/getCurrentRoute"];
            } else {
              this.toastType = "error";
              this.toastMessage = "Error saving record (" + rc + ").";
              this.toastVisible = true;
            }

            this.loadingVisible = false;
          }, 2000);
        },
      },
      isInEditMode: false,
      properties: [],
      typeId: 1, // Default type ID
    };
  },
  computed: {
    currentRecordId() {
      return this.$route.path.substring(this.$route.path.lastIndexOf("/") + 1);
    },
    operatorList() {
      return this.$store.getters["operators/getOperators"];
    },
    areaList() {
      return this.$store.getters["areas/getAreas"];
    },
    productList() {
      return this.$store.getters["properties/getProducts"];
    },
    isEditable() {
      return this.currentRecordId == 0 || this.isInEditMode;
    },

    isDeletingAllowed() {
      return this.routeConfig.isDeleteContainersEnabled && this.isDeleteIconVisible && this.isEditable;
    },
    editButtonOptions() {
      return {
        icon: "edit",
        text: this.isInEditMode ? "Cancel Editing" : "Edit Route",
        visible: this.currentRecordId !== "0",
        onClick: () => {
          this.isInEditMode = !this.isInEditMode;
          this.containers = this.$store.getters["routes/getFilteredSites"];
          this.applySavedOrder();
        },
      }
    },
  },
  methods: {
    onExporting(e) {
      const currentDate = new Date();
      const yyyy = currentDate.getFullYear();
      const mm = String(currentDate.getMonth() + 1).padStart(2, "0");
      const dd = String(currentDate.getDate()).padStart(2, "0");

      e.fileName = `${this.routes.routeName || "RoutesDataExport"
        }_${yyyy}-${mm}-${dd}`;
    },
    saveGridInstance(e) {
      if (e && e.component) {
        this.dataGridInstance = e.component;
      }
    },
    onLoadPanelShown() {
      setTimeout(() => {
        this.loadingVisible = false;
      }, 10000);
    },
    onLoadPanelHidden() { },
    onRoutesUpdate(updatedRoutes) {
      this.routes = updatedRoutes;
      this.applyFilters();
    },
    areFiltersEmpty(message = "Please select filters to apply") {
      if (
        this.routes.operators.length === 0 &&
        this.routes.areas.length === 0 &&
        this.routes.products.length === 0
      ) {
        this.popNotification(message, "warning");
        return true;
      }
      return false;
    },
    async applyFilters(e) {
      this.loadingVisible = true;
      await this.$store.dispatch("routes/applySiteFilters", {
        operators: this.routes.operators,
        areas: this.routes.areas,
        products: this.routes.products,
      });
      this.containers = this.$store.getters["routes/getFilteredSites"];
      this.routes.sortedOrder = this.containers.map((item) => item.containerId);
      this.loadingVisible = false;
      this.isDeleteIconVisible = true;
    },
    applySavedOrder() {
      const visibleContainerIds = this.isEditable ? [...this.routes.sortedOrder, ...this.routes.deletedContainers] : this.routes.sortedOrder;

      if (visibleContainerIds.length === 0) return;
      this.isDeleteIconVisible = true;

      const validContainerIds = new Set(visibleContainerIds);

      if (this.isEditable) {
        this.containers.forEach(container => {
          if (!this.routes.sortedOrder.includes(container.containerId) &&
            !this.routes.deletedContainers.includes(container.containerId)) {
            container.isAdditional = true;
          } else {
            container.isAdditional = false;
          }
        });
      } else {
        this.containers = this.containers.filter((container) =>
          validContainerIds.has(container.containerId)
        );
      }

      this.containers.sort((a, b) => {
        const aIsDeleted = this.routes.deletedContainers.includes(a.containerId);
        const bIsDeleted = this.routes.deletedContainers.includes(b.containerId);
        const aIsAdditional = a.isAdditional;
        const bIsAdditional = b.isAdditional;

        const getPriority = (isDeleted, isAdditional) => {
          if (isDeleted) return 2;
          if (isAdditional) return 1;
          return 0;
        };

        const aPriority = getPriority(aIsDeleted, aIsAdditional);
        const bPriority = getPriority(bIsDeleted, bIsAdditional);

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        if (aPriority === 0) {
          const aIndex = this.routes.sortedOrder.indexOf(a.containerId);
          const bIndex = this.routes.sortedOrder.indexOf(b.containerId);
          return aIndex - bIndex;
        }

        return 0;
      });
    },
    onReorder(e) {
      if (
        this.areFiltersEmpty("You must apply filters before setting your route")
      )
        return;

      const filteredData = this.containers;
      const [movedItem] = filteredData.splice(e.fromIndex, 1);
      filteredData.splice(e.toIndex, 0, movedItem);
      e.component.option("dataSource", filteredData);

      this.routes.sortedOrder = filteredData
        .filter(item => !this.routes.deletedContainers.includes(item.containerId))
        .map(item => item.containerId);
    },
    onCheckboxChange(e, data) {
      if (!e || !data || !data.row || !data.row.data || !data.row.data.containerId) {
        console.warn('Invalid checkbox change event', { e });
        return;
      }

      const containerData = data.row.data;
      const containerId = containerData.containerId;
      const isChecked = e.value;

      if (isChecked) {
        if (containerData.isAdditional) {
          this.routes.sortedOrder.push(containerId);
          containerData.isAdditional = false;
        } else {
          this.restoreContainerById(containerId);
        }
        this.applySavedOrder();
        this.$nextTick(() => {
          if (this.dataGridInstance) {
            this.dataGridInstance.repaint();
          }
        });
      } else {
        this.deleteContainerById(containerId);
      }
    },
    restoreContainerById(containerId) {
      const idx = this.routes.deletedContainers.indexOf(containerId);
      if (idx !== -1) {
        this.routes.deletedContainers.splice(idx, 1);
        this.routes.sortedOrder.push(containerId);
        this.applySavedOrder();
        this.$nextTick(() => {
          if (this.dataGridInstance) {
            this.dataGridInstance.repaint();
          }
        });

        //this.popNotification('Container restored to route.', 'success');
      }
    },
    deleteContainerById(containerId) {
      const handleDelete = () => {
        const sortedIndex = this.routes.sortedOrder.indexOf(containerId);
        if (sortedIndex > -1) {
          this.routes.sortedOrder.splice(sortedIndex, 1);
        }

        if (!this.routes.deletedContainers.includes(containerId)) {
          this.routes.deletedContainers.push(containerId);
        }

        this.applySavedOrder();
        this.$nextTick(() => {
          if (this.dataGridInstance) {
            this.dataGridInstance.repaint();
          }
        });

      };
      handleDelete();

      // if (this.currentRecordId !== "0") {
      //   const container = this.containers.find(c => c.containerId === containerId);
      //   confirm(
      //     `<div style="text-align: center;">
      //       <b>Are you sure you want to remove this container from the route?</b>
      //       <p><strong>Container:</strong> ${container?.containerName || "N/A"}</p>
      //       <p><strong>Operator:</strong> ${container?.operator || "N/A"}</p>
      //       <p><strong>Area:</strong> ${container?.area || "N/A"}</p>
      //     </div>`,
      //     "Remove Container?"
      //   ).then((confirmed) => {
      //     if (confirmed) {
      //       handleDelete();
      //     }

      //   });
      // } else {
      //   handleDelete();
      // }
    },
    async delete(confirmed) {
      if (!confirmed) {
        this.popNotification("Delete record cancelled.", "warning");
        return;
      }

      await this.$store.dispatch("routes/deleteRoute", {
        routeId: this.routes.routeId,
      });
      this.popNotification("Record deleted. ", "success");
      setTimeout(() => {
        this.$router.push({ name: "routes" });
      }, 500);
      this.$emit("recordUpdated");
    },
    refreshData() {
      this.loadPageData();
    },
    setProperties() {
      let retVal = [];
      let flds = this.$store.getters["properties/getProperties"];
      for (let i = 0; i < flds.length; i++) {
        if (flds[i].uiShowList && flds[i].appliesTo == "C" && flds[i].containerTypeId == this.typeId
          && !["Product", "DTE"].includes(flds[i].propertyName)
        ) {
          retVal.push(flds[i].propertyName);
        }
      }
      this.properties = retVal;
    },
    removeFilter() {
      if (this.dataGridInstance) {
        this.dataGridInstance.clearFilter();
        this.dataGridInstance.clearSorting();
      }
    },
    async loadPageData(refresh = false) {
      this.loading = true;
      this.loadingVisible = true;
      try {
        await this.$store.dispatch("properties/loadProperties");
        this.setProperties();

        await this.$store.dispatch("routes/loadRouteConfig");
        this.routeConfig = this.$store.getters["routes/getRouteConfig"];

        await this.$store.dispatch("routes/loadRoute", {
          routeId: this.currentRecordId,
        });
        this.routes = this.$store.getters["routes/getCurrentRoute"];

        await this.$store.dispatch("properties/loadPropertyListByName", {
          name: "Product",
          setterMethod: "setProductsPropertyList",
        });
        await this.$store.dispatch("areas/loadAreas");
        await this.$store.dispatch("operators/loadOperators", {
          forceRefresh: refresh,
        });
        await this.$store.dispatch("routes/applySiteFilters", {
          operators: this.routes.operators,
          areas: this.routes.areas,
          products: this.routes.products,
        });
        this.containers = this.$store.getters["routes/getFilteredSites"];
        this.applySavedOrder();
      } catch (error) {
        this.error = error.message || "Unexpected Error.";
      } finally {
        this.loading = false;
        this.loadingVisible = false;
      }
    },
    popNotification(msg, type) {
      //types ['error', 'info', 'success', 'warning']
      const position = "center";
      const direction = "down-stack";
      notify(
        {
          message: msg,
          type: type,
        },
        {
          position,
          direction,
        }
      );
    },
    onRowPrepared(e) {
      if (!e || !e.data) return;

      if (e.rowType === "data") {
        const containerId = e.data.containerId;
        const isDeleted = this.isDeletedContainer(containerId);
        const isAdditional = e.data.isAdditional;

        if (isDeleted || isAdditional) {
          e.rowElement.style.opacity = "0.5";
          e.rowElement.style.backgroundColor = "rgb(255, 234, 234, 0.2)";
          e.rowElement.style.setProperty("font-style", "italic");
        }
      }
    },
    isDeletedContainer(containerId) {
      if (!containerId || !this.routes || !this.routes.deletedContainers) {
        return false;
      }
      return this.routes.deletedContainers.includes(containerId);
    },
  },
};
</script>

<style scoped>
.menubar {
  padding: 0 0 0 40px;
}

.button-row {
  display: flex;
  justify-content: flex-end;
}

.deleted-row {
  opacity: 0.5;
  font-style: italic;
  background-color: #ffeaea;
}
</style>
